import { useCallback, useSyncExternalStore } from 'react';

/**
 * Cookie notice state, per browser. Crewline only sets strictly necessary cookies (the sign-in
 * session) and keeps preferences in localStorage, so the notice is informational with a single
 * choice to record; "analytics" is kept as a switch for the day an analytics tool is added, and
 * nothing reads it today.
 */
export interface ConsentState {
  /** ISO timestamp of the choice; null until the visitor answers. */
  decidedAt: string | null;
  analytics: boolean;
}

const KEY = 'crewline-cookie-consent';
const listeners = new Set<() => void>();
let state: ConsentState | null = null;
let settingsOpen = false;

function read(): ConsentState {
  if (state) return state;
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<ConsentState>) : {};
    state = { decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : null, analytics: Boolean(parsed.analytics) };
  } catch {
    state = { decidedAt: null, analytics: false };
  }
  return state;
}

function write(next: ConsentState): void {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode: the notice simply shows again next time */
  }
  emit();
}

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const SERVER: ConsentState = { decidedAt: new Date(0).toISOString(), analytics: false };

/** Re-open the notice (footer "Cookies" link, legal pages). */
export function openCookieSettings(): void {
  settingsOpen = true;
  emit();
}

export function useCookieConsent() {
  const s = useSyncExternalStore(subscribe, read, () => SERVER);
  const open = useSyncExternalStore(subscribe, () => settingsOpen, () => false);
  const decide = useCallback((analytics: boolean) => {
    settingsOpen = false;
    write({ decidedAt: new Date().toISOString(), analytics });
  }, []);
  const dismissSettings = useCallback(() => {
    settingsOpen = false;
    emit();
  }, []);
  return { ...s, visible: open || s.decidedAt === null, decide, dismissSettings };
}
